import React, { useState } from 'react';
import { Upload } from 'lucide-react';

const MediaUploader = ({ onUploadComplete }) => {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0); // Mock progress for now
    const [error, setError] = useState(null);

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');

        if (!isVideo && !isImage) {
            setError('Unsupported file type. Please upload images or videos.');
            return;
        }

        setUploading(true);
        setError(null);
        setProgress(10); // Start

        try {
            const token = localStorage.getItem('paradise_token');
            const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';

            // 1. Get Direct Upload URL
            const endpoint = isVideo ? '/api/upload-video' : '/api/upload-image';
            const res = await fetch(`${API_BASE}${endpoint}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Failed to get upload token');
            const data = await res.json();
            const uploadURL = data.uploadURL;
            const assetId = isVideo ? data.uid : data.id;

            setProgress(30); // Got URL

            // 2. Upload to Cloudflare
            const formData = new FormData();
            formData.append('file', file);

            const uploadRes = await fetch(uploadURL, {
                method: 'POST',
                body: formData
            });

            // Parse response regardless of status to get details
            const uploadData = await uploadRes.json().catch(() => ({}));

            if (!uploadRes.ok) {
                console.error("Cloudflare Upload Error:", uploadData);
                throw new Error(uploadData?.errors?.[0]?.message || 'Cloudflare upload failed');
            }

            setProgress(100);

            // 3. Notify Parent
            let variants = [];
            if (!isVideo && uploadData.result && uploadData.result.variants) {
                variants = uploadData.result.variants;
            }

            const mediaItem = {
                type: isVideo ? 'video' : 'image',
                cloudflareId: assetId,
                filename: file.name,
                mimeType: file.type,
                variants: variants
            };

            onUploadComplete(mediaItem);
            setUploading(false);

        } catch (err) {
            console.error(err);
            setError(err.message);
            setUploading(false);
        }
    };

    return (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-black transition-colors cursor-pointer relative bg-gray-50">
            <input
                type="file"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={handleFileSelect}
                disabled={uploading}
                accept="image/*,video/*"
            />

            {uploading ? (
                <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black mb-2"></div>
                    <span className="text-xs font-bold uppercase tracking-widest">Uploading... {progress}%</span>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center space-y-2 pointer-events-none">
                    <Upload className="text-gray-400" size={24} />
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Click to Upload Media</span>
                    <span className="text-[10px] text-gray-400">Images or Videos (Max 1GB)</span>
                </div>
            )}

            {error && (
                <div className="absolute bottom-2 left-0 right-0 text-center text-red-500 text-xs font-bold">
                    {error}
                </div>
            )}
        </div>
    );
};

export default MediaUploader;
